import type { ListBuilderColumn } from '../../types';
import { TRIP_TYPE_VALUE_MAPPING } from './shared';

/**
 * Trip name column with link.
 *
 * PD-16209 — trips have their own name (inherited from the route when built
 * from one, editable on the trip), and the trips list returns it as `name`,
 * falling back to the default name for a row not yet backfilled. It is never
 * blank, so every row links to its trip. `objectTable: 'route'` is the trips
 * list's SQL alias for the trip table, not the route table.
 */
export const createTripNameColumn = (): ListBuilderColumn => ({
	id: 'route-name',
	headers: [{ id: 'route-name-header', text: 'Name' }],
	fields: [
		{
			id: 'name-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: 'route' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
			link: { type: 'object', enabled: true },
		},
	],
	sortable: true,
	width: 200,
});

/**
 * Date column
 */
export const createDateColumn = (): ListBuilderColumn => ({
	id: 'date',
	headers: [{ id: 'date-header', text: 'Date' }],
	fields: [
		{
			id: 'date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'date', objectTable: 'route' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'date',
			formatOptions: { dateStyle: 'short' },
		},
	],
	sortable: true,
	width: 120,
});

/**
 * Driver column
 */
export const createDriverColumn = (): ListBuilderColumn => ({
	id: 'driver',
	headers: [{ id: 'driver-header', text: 'Driver' }],
	fields: [
		{
			id: 'driver-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'driverName', objectTable: 'route' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	width: 180,
});

/**
 * Vehicle column
 */
export const createVehicleColumn = (): ListBuilderColumn => ({
	id: 'vehicle',
	headers: [{ id: 'vehicle-header', text: 'Vehicle' }],
	fields: [
		{
			id: 'vehicle-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'vehicleName', objectTable: 'route' },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	width: 150,
});

/**
 * Status column with badge
 */
export const createStatusColumn = (): ListBuilderColumn => ({
	id: 'status',
	headers: [{ id: 'status-header', text: 'Status' }],
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'activeTrip', objectTable: 'route' },
			},
			// PD-13747 — pause/return-aware status via the shared
			// trip-status-badge cell + getTripStatusPresentation, so a paused
			// trip reads "Paused · <type>" instead of just "In Progress". Reads
			// the row's `activeTrip` overlay (always present for a trip). Not
			// sortable — the value is a derived object; the "Status" sort preset
			// still orders by the real `status` column.
			displayStyle: 'custom',
			customComponent: { id: 'trip-status-badge' },
		},
	],
	sortable: false,
	width: 130,
});

/**
 * PD-16797 — trip type (Delivery / Service) as a badge. Sortable: the
 * column is a real trip column, so the server can order by it.
 */
export const createTripTypeColumn = (): ListBuilderColumn => ({
	id: 'trip-type',
	headers: [{ id: 'trip-type-header', text: 'Type' }],
	fields: [
		{
			id: 'trip-type-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'tripType', objectTable: 'route' },
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'value-mapping',
					valueMapping: TRIP_TYPE_VALUE_MAPPING,
				},
			},
			displayStyle: 'badge',
		},
	],
	sortable: true,
	width: 110,
});

/**
 * Stops column
 */
export const createStopsColumn = (): ListBuilderColumn => ({
	id: 'stops',
	headers: [{ id: 'stops-header', text: 'Stops' }],
	fields: [
		{
			id: 'stops-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'totalStops', objectTable: 'route' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	width: 100,
});

/**
 * Warehouse column
 */
export const createWarehouseColumn = (): ListBuilderColumn => ({
	id: 'warehouse',
	headers: [{ id: 'warehouse-header', text: 'Warehouse' }],
	fields: [
		{
			id: 'warehouse-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'warehouseName', objectTable: 'route' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'muted' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	width: 180,
});

/**
 * Create all route columns with date column
 */
export const createTripColumnsWithDate = (): Array<ListBuilderColumn> => [
	createTripNameColumn(),
	createDateColumn(),
	createDriverColumn(),
	createVehicleColumn(),
	createTripTypeColumn(),
	createStatusColumn(),
	createStopsColumn(),
	createWarehouseColumn(),
];

/**
 * Create route columns without date column (for today's routes)
 */
export const createTripColumnsWithoutDate = (): Array<ListBuilderColumn> => [
	createTripNameColumn(),
	createDriverColumn(),
	createVehicleColumn(),
	createTripTypeColumn(),
	createStatusColumn(),
	createStopsColumn(),
	createWarehouseColumn(),
];
